import { motion, ResolvedValues } from "framer-motion";
import {
  defaultBgVariants,
  defaultPanelVariants,
} from "../common/defaultValues";
import ItemToValidate from "./ItemToValidate";
import { capitaliseFirstChar } from "utils/utils";
import { useRef, useState } from "react";
import { ValidateLessonContentsPopUpContext } from "components/utils/Contexts";

interface ValidatingLessonContentsProps {
  lessonType: string;
  contentToBeChecked: {
    [content: string]: {
      status: "successful" | "loading" | "error" | "warning";
      report?: any;
    };
  };
  onBgClick: () => void;
  onCloseClick: () => void;
}

// Parameters
const PANEL_MOVEMENT_ANIMATION_TIME = 0.5; // Seconds;

const ValidatingLessonContents: React.FC<ValidatingLessonContentsProps> = ({
  lessonType,
  contentToBeChecked,
  onBgClick,
  onCloseClick,
}) => {
  // States definition
  const [detailToShow, setDetailToShow] = useState<string | null>(null);
  const [detailPanelSizes, setDetailPanelSizes] = useState({
    width: 0,
    height: 0,
  });

  // Reference for the panel container component
  const detailPanelRef = useRef<HTMLDivElement>(null);

  // Lesson name definition
  const lessonName = capitaliseFirstChar(lessonType.slice(0, -1));

  // Get the array of content to be checked
  const contentList = Object.keys(contentToBeChecked);

  const checkStatus = () => {
    // Get all of the status available in the contentToBeChecked object
    const statusList = Object.values(contentToBeChecked);

    // Define the status based on the presence of status in the list
    if (statusList.some((item) => ["error", "warning"].includes(item.status)))
      return "error";
    else return "loading";
  };

  // Variable to check if all of the states are successful,
  // or they have some error
  const status = checkStatus();

  /**
   * Callback function used when clicking on a detail error button
   */
  const onErrorDetailClick = (contentClicked: string) => {
    // And set the detail of the content clicked
    setDetailToShow(contentClicked);
  };

  /**
   * Callback Function used when clicking on the close button of
   * the error detail panel
   */
  const onCloseErrorDetail = () => {
    // And set the detail of the content clicked
    setDetailToShow(null);
  };

  return (
    <ValidateLessonContentsPopUpContext.Provider
      value={{
        detailToShow,
        detailPanelRef,
        detailPanelSizes,
        PANEL_MOVEMENT_ANIMATION_TIME,
      }}
    >
      <motion.div
        key="saving-blurred-background"
        className="fixed inset-0 w-full h-full bg-neutral-500 bg-opacity-40 backdrop-blur-[2px] z-20 flex flex-col flex-grow"
        variants={defaultBgVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        transition={{ duration: 0.1, ease: "easeOut" }}
        onClick={onBgClick}
      />
      <motion.div
        key="validating-lessons-modal"
        className="fixed w-fit h-fit left-0 right-0 ml-auto mr-auto bottom-5 p-6 pb-8 bg-white rounded-3xl inline-flex z-30 overflow-hidden"
        variants={defaultPanelVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        transition={{ duration: 0.1, ease: "easeOut" }}
      >
        <div className="w-[402px] inline-flex flex-col justify-start items-start gap-10">
          <div className="self-stretch inline-flex items-center justify-center">
            <div className="text-center text-neutral-600 text-2xl font-semibold font-sans leading-7">
              Validating contents
            </div>
          </div>
          {status === "loading" ? (
            <div className="justify-start text-neutral-600 text-base font-normal font-sans leading-tight">
              Hang tight while we check the content one last time before
              saving...
            </div>
          ) : (
            <div className="justify-start text-neutral-600 text-base font-normal font-sans leading-tight">
              Content revision required. Please check the sections marked for
              review.
            </div>
          )}
          {/* Check if the content has been defined */}
          {["headerImage", "title"].some((item: string) =>
            contentList.includes(item)
          ) && (
            <div className="w-full inline-flex flex-col gap-4 items-start">
              <div className="self-stretch justify-start text-neutral-700 text-base font-semibold font-sans leading-tight">
                Basic details
              </div>
              {contentList.includes("headerImage") && (
                <ItemToValidate
                  key="header-image-item"
                  text="Header image"
                  contentType="headerImage"
                  info={contentToBeChecked["headerImage"]}
                  onDetailClick={onErrorDetailClick}
                  onCloseErrorDetail={onCloseErrorDetail}
                />
              )}
              {contentList.includes("title") && (
                <ItemToValidate
                  key="title-item"
                  text={`${lessonName} name`}
                  contentType="title"
                  info={contentToBeChecked["title"]}
                  onDetailClick={onErrorDetailClick}
                  onCloseErrorDetail={onCloseErrorDetail}
                />
              )}
            </div>
          )}
          {/* Check if the content has been defined */}
          {["badgeImage", "description"].some((item: string) =>
            contentList.includes(item)
          ) && (
            <div className="w-full inline-flex flex-col gap-4 items-start">
              <div className="self-stretch justify-start text-neutral-700 text-base font-semibold font-sans leading-tight">
                Additional details
              </div>
              {contentList.includes("badgeImage") && (
                <ItemToValidate
                  key="badge-image-item"
                  text="Badge image"
                  contentType="badgeImage"
                  info={contentToBeChecked["badgeImage"]}
                  onDetailClick={onErrorDetailClick}
                  onCloseErrorDetail={onCloseErrorDetail}
                />
              )}
              {contentList.includes("description") && (
                <ItemToValidate
                  key="description-item"
                  text={`${lessonName} description`}
                  contentType="description"
                  info={contentToBeChecked["description"]}
                  onDetailClick={onErrorDetailClick}
                  onCloseErrorDetail={onCloseErrorDetail}
                />
              )}
            </div>
          )}
          {["planDocument", "videoUrl", "initialCode", "finalCode"].some(
            (item: string) => contentList.includes(item)
          ) && (
            <div className="w-full inline-flex flex-col gap-4 items-start">
              <div className="self-stretch justify-start text-neutral-700 text-base font-semibold font-sans leading-tight">
                Additional attachments
              </div>
              {contentList.includes("planDocument") && (
                <ItemToValidate
                  key="lesson-pdf-item"
                  text={`${lessonName} PDF`}
                  contentType="planDocument"
                  info={contentToBeChecked["planDocument"]}
                  onDetailClick={onErrorDetailClick}
                  onCloseErrorDetail={onCloseErrorDetail}
                />
              )}
              {contentList.includes("videoUrl") && (
                <ItemToValidate
                  key="video-url-item"
                  text="Video URL"
                  contentType="videoUrl"
                  info={contentToBeChecked["videoUrl"]}
                  onDetailClick={onErrorDetailClick}
                  onCloseErrorDetail={onCloseErrorDetail}
                />
              )}
              {contentList.includes("initialCode") && (
                <ItemToValidate
                  key="initial-code-item"
                  text="Initial code"
                  contentType="initialCode"
                  info={contentToBeChecked["initialCode"]}
                  onDetailClick={onErrorDetailClick}
                  onCloseErrorDetail={onCloseErrorDetail}
                />
              )}
              {contentList.includes("finalCode") && (
                <ItemToValidate
                  key="final-code-item"
                  text="Final code"
                  contentType="finalCode"
                  info={contentToBeChecked["finalCode"]}
                  onDetailClick={onErrorDetailClick}
                  onCloseErrorDetail={onCloseErrorDetail}
                />
              )}
            </div>
          )}
          {/* Show the close button just when there's an error */}
          {status === "error" && (
            <motion.div
              key="close-button"
              initial={{ y: "100%", height: 0 }}
              animate={{ y: 0, height: 46 }}
              exit={{ height: 0 }}
              className="self-stretch inline-flex px-5 py-3 justify-center items-center"
            >
              <div
                className="font-sans text-lg font-semibold text-secondary-600 hover:text-secondary-700 hover:underline cursor-pointer"
                onClick={onCloseClick}
              >
                Close
              </div>
            </motion.div>
          )}
        </div>
        {/* Define the container that will held the moving detail panels */}
        <motion.div
          ref={detailPanelRef}
          initial={{ width: 0, height: 0 }}
          animate={{
            width: !!detailToShow ? 371 + 24 : 0,
            height: !!detailToShow ? 700 : 20,
          }}
          exit={{ width: 0, height: 0 }}
          onUpdate={(latest: ResolvedValues) => {
            setDetailPanelSizes({
              width: latest.width as number,
              height: latest.height as number,
            });
          }}
          className="max-h-[80vh] overflow-hidden"
          transition={{
            duration: PANEL_MOVEMENT_ANIMATION_TIME,
            ease: "easeOut",
          }}
        />
      </motion.div>
    </ValidateLessonContentsPopUpContext.Provider>
  );
};

export default ValidatingLessonContents;
